// src/hooks/useAuth.tsx
import { Button } from "@mui/material";
import { closeSnackbar, useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  clearToken,
  fetchUserDetails,
  logout,
  setAuthTokens,
} from "../redux/authSlice";
import { AppDispatch } from "../redux/store";
import { RootState } from "../types";
import logger from "utils/logger";
import { useTranslation } from "../i18n";

interface JwtToken {
  exp: number;
}

export const useAuth = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch<AppDispatch>();

  const handleLogout = () => {
    dispatch(clearToken());
    dispatch(logout());
  };

  return {
    isAuthenticated: !!token,
    token,
    user,
    logout: handleLogout,
  };
};

// New helper for social login
export const useSocialAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleSocialAuthSuccess = async (data: any) => {
    dispatch(setAuthTokens({ access: data.access, refresh: data.refresh }));
    const resultAction = await dispatch(fetchUserDetails());
    if (fetchUserDetails.fulfilled.match(resultAction)) {
      const user = resultAction.payload;
      if (user?.access_tier === "waitlisted") {
        // Redirect back to landing — the beta gate card will auto-show there
        navigate("/");
        return;
      }
    }
    enqueueSnackbar(t("auth.login_success", "Login successful!"), { variant: "success" });
    navigate("/dashboard");
  };

  const handleSocialAuthError = async (
    provider: string,
    token: string,
    res: Response,
    data?: any
  ) => {
    if (res.status === 409 && data?.code === "account_exists") {
      sessionStorage.setItem(
        "pending_social_connect",
        JSON.stringify({ provider, token })
      );
      enqueueSnackbar(
        t("auth.social_duplicate", "This email is already registered with another account. Please log in using your original method."),
        {
          variant: "warning",
          persist: true,
          action: (key) => (
            <Button
              onClick={() => {
                closeSnackbar(key);
                navigate("/login");
              }}
            >
              {t("auth.ok", "OK")}
            </Button>
          ),
        }
      );
    } else {
      logger.error("❌ Social login failed:", data);
      enqueueSnackbar(t("auth.social_failed", "Social login failed. Please try again."), {
        variant: "error",
      });
      navigate("/login");
    }
  };

  return { handleSocialAuthSuccess, handleSocialAuthError };
};
